import React, { useState, useEffect, useRef } from "react";
import MetaTags from "react-meta-tags";
import {
  Col,
  Row,
  Button,
  Table,
  Space,
  Tag,
  Modal,
  Input,
  Typography,
} from "antd";
import {
  DownloadOutlined,
  EditOutlined,
  DeleteOutlined,
  ClearOutlined,
  SearchOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { toast, ToastContainer } from "react-toastify";
import { useSelector, useDispatch } from "react-redux";
import BreadcrumbComponent from "../../components/BreadCrumb";
import api from "../../helpers";
import * as masterAction from "../../store/Masters/actions";
import SimpleReactValidator from "simple-react-validator";
import { isEmpty } from "lodash";
import "../LeaveAndPermissions/permission.css";

const BCrumb = [{ to: "/", title: "Home" }, { title: "Role" }];

const Role = () => {
  const [selectedRow, setSelectedRow] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalType, setModalType] = useState("");
  const [disabled, setDisabled] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const simpleValidator = useRef(new SimpleReactValidator());
  const [, forceUpdate] = React.useState();
  const [roleId, setRoleId] = useState();
  const [downloadLoader, setDownloadLoader] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [addLoading, setAddLoading] = useState(false);

  const [role, setRole] = useState({ roleName: "", description: "" });

  const specificDispatch = useDispatch();
  const dispatch = useDispatch();

  //dropdown state from reducer
  const updateState = useSelector((state) => state.Masters);
  const { roleList = [] } = updateState;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    specificDispatch(masterAction.loadRole({ status: "BOTH" }));
  }, [specificDispatch]);

  // modal handling
  const showModalOpen = (type) => {
    setModalType(type);
    setIsModalVisible(true);
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
    setModalType("");
  };

  const onReset = () => {
    setResetLoading(true);
    setTimeout(() => {
      setRole({
        roleName: "",
        description: "",
      });
      simpleValidator.current.hideMessages();
      forceUpdate(1);
      setSearchInput("");
      setResetLoading(false);
      setIsEdit(false);
      setRoleId(null);
    }, 1000);
    dispatch(masterAction.loadRole({ status: "BOTH" }));
    setDisabled(false);
  };

  const handleSubmit = () => {
    if (!simpleValidator.current.allValid()) {
      simpleValidator.current.showMessages();
      forceUpdate(1);
      return;
    }

    setAddLoading(true);
    setDisabled(true);
    setTimeout(() => setDisabled(false), 4000);

    const payload = {
      role: role.roleName,
      ...(role.description && { description: role.description }),
    };

    if (!isEdit) {
      api
        .post("/protected/addrole", payload)
        .then((res) => {
          if (res.status === 200 || res.status === 201) {
            dispatch(masterAction.loadRole({ status: "BOTH" }));
            toast.success("Role added successfully", {
              containerId: "toast-1",
            });
            setRole({ roleName: "", description: "" });
            simpleValidator.current.hideMessages();
            forceUpdate(1);
          } else {
            toast.warning(
              res?.data?.message ||
                res?.data?.msg ||
                "Unexpected error",
              {
                containerId: "toast-1",
              }
            );
          }
        })
        .catch((err) => {
          const errorMsg =
            err?.response?.data?.message ||
            "An error occurred while adding the role";
          toast.error(errorMsg,{
                containerId: "toast-1",
              });
        })
        .finally(() => {
          setAddLoading(false);
        });
    } else {
      const employeePayload = {
        role: role.roleName,
        description: role.description,
      };

      api
        .post(`/protected/updaterole/${roleId}`, employeePayload)
        .then((response) => {
          if (response.status === 200 || response.status === 201) {
            dispatch(masterAction.loadRole({ status: "BOTH" }));
            toast.success("Role updated successfully", {
              containerId: "toast-1",
            });
            setRole({ roleName: "", description: "" });
            setIsEdit(false);
            setRoleId(null);
            simpleValidator.current.hideMessages();
            forceUpdate(1);
          } else {
            toast.warning(response?.data?.msg || "");
          }
        })
        .catch((err) => {
          const errorMsg =
            err?.response?.data?.message ||
            "An error occurred while updating the role";
          toast.error(errorMsg, { containerId: "toast-1" });
        })
        .finally(() => {
          setAddLoading(false);
        });
    }
  };

  const handleEdit = (roleData) => {
    setIsEdit(true);
    setRole({
      roleName: roleData.roleName,
      description: roleData.description,
    });
  };

  //modal input handler
  const handleInput = (e) => {
    const { name, value } = e.target;

    setRole((prevData) => ({
      ...prevData,
      [name]: value,
    }));

    simpleValidator.current.hideMessageFor(name);
    forceUpdate(1);
  };

  // search
  const handleSearch = () => {
    const payload = {
      search: searchInput,
      status: "BOTH",
    };
    setSearchLoading(true);

    setTimeout(() => {
      setSearchLoading(false);
    }, 500);
    dispatch(masterAction.loadRole(payload));
  };

  const columns = [
    {
      title: (
        <span style={{ whiteSpace: "nowrap" }} className="poppins-txt">
          S.No
        </span>
      ),
      dataIndex: "sno",
      width: 5,
      className: "poppins-txt-lg",
    },
    {
      title: <span className="poppins-txt">Action</span>,
      dataIndex: "action",
      width: 70,
      render: (_, record) => (
        <Space style={{ gap: "8px" }}>
          {/* Edit Button */}
          <Button
            style={{
              background: "#34c38f",
              border: "none",
              minWidth: 30,
              height: 30,
              padding: "2px 4px",
              fontSize: "0.75rem",
            }}
            onClick={() => {
              handleEdit(record);
              setRoleId(record._id);
            }}
          >
            <EditOutlined style={{ color: "#fff", fontSize: 14 }} />
          </Button>

          {/* Delete Button */}
          <Button
            style={{
              background: "#f46a6a",
              border: "none",
              minWidth: 30,
              height: 30,
              padding: "2px 4px",
              fontSize: "0.75rem",
            }}
            onClick={() => {
              showModalOpen("delete");
              setRoleId(record._id); // single delete
              setSelectedRow([]); // make sure no bulk delete happens
            }}
          >
            <DeleteOutlined style={{ color: "#fff", fontSize: 14 }} />
          </Button>
        </Space>
      ),
    },
    {
      title: <span className="poppins-txt">Role</span>,
      dataIndex: "roleName",
      width: 130,
      className: "poppins-txt-lg",
    },
    {
      title: <span className="poppins-txt">Description</span>,
      dataIndex: "description",
      width: 130,
      className: "poppins-txt-lg",
    },
    {
      title: (
        <span style={{ whiteSpace: "nowrap" }} className="poppins-txt">
          Status
        </span>
      ),
      dataIndex: "status",
      width: 100,
      render: (status) => {
        const isActive = status?.toLowerCase() === "active";
        return (
          <Tag
            style={{
              backgroundColor: isActive ? "#e6f4d7" : "#fae1e1",
              color: isActive ? "#34c38f" : "#f46a6a",
              fontWeight: 500,
              width: 60,
              height: 20,
              paddingTop: "2px",
              fontSize: "11px",
              textAlign: "center",
              lineHeight: "16px",
              borderRadius: 4,
              border: "none",
              fontFamily: "Poppins",
            }}
          >
            {isActive ? "Active" : "Inactive"}
          </Tag>
        );
      },
    },
  ];

  //download functionality
  const downloadFile = ({ data, fileName, fileType }) => {
    const blob = new Blob([data], { type: fileType });

    const a = document.createElement("a");
    a.download = fileName;
    a.href = window.URL.createObjectURL(blob);
    const clickEvt = new MouseEvent("click", {
      view: window,
      bubbles: true,
      cancelable: true,
    });
    a.dispatchEvent(clickEvt);
    a.remove();
  };

  const exportToCsv = async () => {
    setDownloadLoader(true);

    const searchFilter = {};

    try {
      const res = await api.post("/protected/getallrole", searchFilter);
      const role = res.data?.roles || [];
      const mappedData = role.map((data, index) => ({
        _id: data._id,
        sno: index + 1,
        roleName: data.role || "--",
        description: data.description || "--",
        status: data.isActive === true ? "Active" : "Inactive",
      }));

      // Only include fields for CSV that you want:
      const headers = ["S.No", "Role Name", "Description", "Status"];

      const csvRows = mappedData.map((item) => [
        item.sno,
        item.roleName,
        item.description,
        item.status,
      ]);

      const csvContent = [
        headers.join(","),
        ...csvRows.map((row) => row.join(",")),
      ].join("\n");

      downloadFile({
        data: csvContent,
        fileName: "role-details.csv",
        fileType: "text/csv",
      });
    } catch (error) {
      if (error) {
        setDownloadLoader(false);
      }
    } finally {
      setDownloadLoader(false);
    }
  };

  //delete
  const handleUpdateStatus = (statusType) => {
    setDisabled(true);
    setTimeout(() => {
      setDisabled(false); // Re-enable after 4 seconds
    }, 4000);

    const statusMap = {
      delete: 3,
      active: 1,
      inactive: 2,
    };

    const toastMessage = {
      delete: "Role deleted successfully",
      active: "Role activated successfully",
      inactive: "Role deactivated successfully",
    };

    const statusValue = statusMap[statusType];
    if (!statusValue) return;
    const id = !isEmpty(selectedRow) ? selectedRow : [roleId];
    const payload = {
      id: id, // use selectedRow instead of hardcoded ID
      status: statusValue,
    };
    api
      .post(`/protected/deleterole`, payload)
      .then((response) => {
        if (response.status === 200 || response.status === 201) {
          specificDispatch(masterAction.loadRole({ status: "BOTH" }));
          toast.success(toastMessage[statusType], { containerId: "toast-1" });
          setSelectedRow([]);
          setRoleId(null);
          handleModalClose();
        } else {
          toast.warning(response?.data?.msg || "");
        }
        return response.data;
      })
      .catch((err) => {
         setSelectedRow([]);
          handleModalClose();
        const errorMsg =
       err?.response?.data?.error||err?.response?.data?.message ||
          "An error occurred while deleting the role details";
        toast.error(errorMsg);
        return Promise.reject(err);
      });
  };

  const tableData = !isEmpty(roleList)
    ? roleList.map((data, index) => ({
        _id: data._id,
        sno: index + 1,
        roleName: data.role || "--",
        description: data.description || "--",
        status: data.status === 1 ? "Active" : "Inactive",
      }))
    : [];

  //status handling

  // const handleUpdateStatus = (statusType) => {
  //   setDisabled(true); // Disable the button
  //   setTimeout(() => {
  //     setDisabled(false); // Re-enable after 4 seconds
  //   }, 4000);
  // };

  return (
    <>
      <div className="pl-5">
        <MetaTags>
          <title>Role | Mequals</title>
        </MetaTags>
      </div>
      <Row>
        <BreadcrumbComponent title="Role" items={BCrumb} />
      </Row>

      <div
        style={{
          background: "#ffffff",
          margin: "4px 8px",
          padding: "16px",
          borderRadius: "8px 8px 0px 0px",
        }}
      >
        <Row gutter={[16, 16]} style={{ marginTop: "2px" }}>
          <Col xs={24} sm={12} md={8} lg={6} xl={6}>
            <Typography className="poppins-txt">
              Role
              <span style={{ color: "red", marginLeft: "2px" }}>*</span>
            </Typography>
            <Input
              placeholder="Enter Role Name"
              allowClear
              style={{ width: "100%", fontFamily: "Poppins", marginTop: "5px" }}
              name="roleName"
              type="text"
              value={role.roleName}
              className="custom-select"
              onChange={(e) => handleInput(e)}
              autoComplete="off"
            />
            <span
              style={{
                color: "#ef3022",
                fontSize: "11px",
                marginTop: "8px",
                fontFamily: "poppins",
                display: "block",
              }}
            >
              {simpleValidator.current.message(
                "roleName",
                role.roleName,
                "required"
              )}
            </span>
          </Col>

          <Col xs={24} sm={12} md={8} lg={6} xl={6}>
            <Typography className="poppins-txt">Description</Typography>
            <Input
              placeholder="Enter Description"
              allowClear
              style={{ width: "100%", fontFamily: "Poppins", marginTop: "5px" }}
              name="description"
              className="custom-select"
              type="text"
              value={role.description}
              onChange={(e) => handleInput(e)}
              autoComplete="off"
            />
          </Col>
          <Col xs={24} sm={24} md={24} lg={24} xl={24}>
            <div
              className="mt-1"
              style={{ display: "flex", justifyContent: "flex-end" }}
            >
              <Button
                className="custom-btn"
                size="middle"
                type="primary"
                style={{ marginRight: "12px" }}
                onClick={handleSubmit}
                disabled={disabled}
                loading={addLoading}
              >
                {isEdit ? (
                  "Update"
                ) : (
                  <span>
                    <PlusOutlined></PlusOutlined>Add
                  </span>
                )}
              </Button>
              <Button
                className="custom-btn"
                style={{ background: "#FCE05A", color: "white" }}
                size="middle"
                htmlType="submit"
                onClick={onReset}
                loading={resetLoading}
              >
                <ClearOutlined />
                Clear
              </Button>
            </div>
          </Col>
        </Row>
      </div>

      <div
        style={{
          background: "#ffffff",
          margin: "20px 8px 15px 8px",
          padding: "16px",
        }}
      >
        <Row
          justify="space-between"
          align="middle"
          style={{ marginBottom: 16 }}
        >
          <Col>
            <Input
              placeholder="Search..."
              allowClear
              style={{ width: 250, fontFamily: "Poppins" }}
              name="searchInput"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              type="text"
              autoComplete="off"
            />

            <Button
              icon={<SearchOutlined />}
              type="primary"
              style={{ marginLeft: 8 }}
              onClick={handleSearch}
            />
          </Col>

          <Col>
            <Button
              className="custom-btn"
              size="medium"
              type="primary"
              icon={<DownloadOutlined />}
              onClick={exportToCsv}
              loading={downloadLoader}
            >
              Download
            </Button>
          </Col>
        </Row>

        <Table
          columns={columns}
          loading={searchLoading}
          dataSource={tableData}
          className="poppins-txt"
          rowKey={(record) => record._id}
          rowClassName={"text-[12px]"}
          pagination={{
            // make sure you provide total count from API
            showSizeChanger: true,
            pageSizeOptions: ["5", "10", "20", "30"],
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
          }}
          sortDirections={["ascend", "descend"]}
          showSorterTooltip={"sorter-icon"}
          scroll={{
            x: 765,
          }}
          rowSelection={{
            selectedRowKeys: selectedRow,
            preserveSelectedRowKeys: true,
            onSelectAll: (selected, selectedRows) => {
              const newSelecteds = selected
                ? selectedRows.map((n) => n._id)
                : [];
              setSelectedRow(newSelecteds);
            },
            onSelect: (record, selected) => {
              let newSelecteds = selected
                ? [...selectedRow, record._id]
                : selectedRow.filter((id) => id !== record._id);
              setSelectedRow(newSelecteds);
            },
          }}
        />

        <Space size={4}>
          <Button
            style={{
              marginTop: "20px",
              marginRight: "12px",
              background: "#f46a6a",
            }}
            type="primary"
            size="medium"
            className="custom-btn"
            onClick={() => showModalOpen("delete")}
          >
            Delete
          </Button>

          <Button
            style={{
              marginTop: "20px",
              background: "#34c38f",
              marginRight: "12px",
            }}
            type="primary"
            size="medium"
            className="custom-btn"
            onClick={() => showModalOpen("active")}
          >
            Active
          </Button>

          <Button
            style={{
              marginTop: "20px",
            }}
            type="primary"
            size="medium"
            className="custom-btn"
            onClick={() => showModalOpen("inactive")}
          >
            Inactive
          </Button>
        </Space>

        <Modal
          open={isModalVisible}
          onCancel={handleModalClose}
          title={"Confirmation"}
          cancelText="Cancel"
          className="poppins-txt custom-active-modal"
          centered
          width={450}
          footer={null} // Remove the default footer
          // style={{ padding: 0, marginLeft: "600px" }} // Remove padding from modal
        >
          <div className="custom-modal-content">
            <p>Are you sure you want to {modalType} this Role?</p>
          </div>

          {/* Custom footer with cancel and confirm buttons */}
          <div
            className="custom-modal-footer"
            style={{
              borderTop: "1px solid #ccc",
              padding: "10px 16px",
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <Button
              className="custom-btn"
              onClick={handleModalClose}
              style={{
                backgroundColor: "#f46a6a",
                color: "#fff",
                border: "none",
                marginRight: "10px",
              }}
            >
              Close
            </Button>
            <Button
              className="custom-btn"
              disabled={disabled}
              // onClick={handleModalClose}
              onClick={() => handleUpdateStatus(modalType)}
              style={{
                backgroundColor: "#34c38f",
                color: "#fff",
                border: "none",
              }}
            >
              Confirm
            </Button>
          </div>
        </Modal>
      </div>
      <ToastContainer
        containerId="toast-1"
        position="top-right"
        autoClose={1500}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        style={{
          fontSize: "12px",
        }}
        pauseOnHover
        theme="dark"
      />
    </>
  );
};

export default Role;
